import {
  Alert,
  Box,
  Button,
  Card,
  Snackbar,
  Slide,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { FaCaretLeft, FaCaretRight } from "react-icons/fa";
import { MdEdit, MdOutlinePolicy } from "react-icons/md";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import AerialStep from "../../components/Aereo";
import { AlertModal } from "../../components/AlertModal";
import Criativo from "../../components/Criativo/Criativo";
import Producao from "../../components/Criativo/Producao";
import Logistica from "../../components/Logistics";
import EstudoDeVerba from "../../features/estudo-de-verba/EstudoDeVerba";
import {
  EstudoAutosaveProvider,
  useEstudoAutosaveContext,
} from "../../features/estudo-de-verba/estudo-autosave-context";
import AuditModal from "../../components/novos/Audit/Modal/AuditModal";
import NegotiationAuditModal from "../../components/novos/Budget/NegotiationAuditModal";
import ClosingAuditModal from "../../components/novos/Budget/ClosingAuditModal";
import BudgetStep from "../../components/novos/Budget";
import BudgetBottomBar, {
  useBudgetBottomBarVisible,
} from "../../components/novos/Budget/BudgetBottomBar";
import RSVPStep from "../../components/novos/RSVP";
import ProjectStep from "../../components/Project";
import ProjectStatusModal from "../../components/Project/templates/ProjectStatusModal";
import ProjectStatusTimeline from "../../components/Project/ProjectStatusTimeline";
import SummaryStep from "../../components/Summary";
import { useProject } from "../../contexts/project.context";
import { BRIEFING_STATUS_OPTIONS } from "../../utils/briefingStatus";
import BoothStep from "../../components/novos/Booth";
import ProjectStoragePage from "../../features/project-storage/components/ProjectStoragePage";
import ProjectOverview from "../../features/project-overview/components/ProjectOverview";
import {
  formatEventLocation,
  formatEventPeriod,
  getNextStepTab,
} from "../../features/project-overview/utils/overview";
import { normalizeProjectType } from "../../utils/opcoesBriefingInicial";
import { useBudget } from "../../contexts/BudgetContext/projectbudgets.context";
import { UnsavedChangesModal } from "../../components/Summary/components/UnsavedChangesModal";
import { toast } from "react-toastify";
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ComponentType,
} from "react";
import {
  useProjectLastEditorAudit,
  useProjectTimelineAudit,
} from "../../queries/hooks";
import { TabActive } from "../../types";
import { useServices } from "../../contexts/ServicesContext/projectservices.context";
import type { BudgetListItem } from "../../utils/budget-adapters";

/** Budget id prefix from `detailedBudget` (`{id}-version-{n}`). */
function parseBudgetIdFromDetailed(
  detailedBudget: string | undefined
): string | undefined {
  if (!detailedBudget) return undefined;
  const id = detailedBudget.split("-")[0];
  return id || undefined;
}

/**
 * FE-P1-08: resolve a list row for Orçamento / Negociação / Fechamento.
 * Prefers `_id` (active selection) so tab switches keep the same budget when
 * it qualifies; falls back to first matching summary. Uses only summary-safe
 * fields (`_id`, `versionHistory`) — never `budgetServices`.
 */
function findListBudgetForTab(
  budgetList: BudgetListItem[] | undefined,
  targetTab: TabActive,
  preferredId?: string
): BudgetListItem | undefined {
  if (!budgetList?.length) return undefined;

  const matchesTab = (el: BudgetListItem): boolean => {
    const history = el.versionHistory ?? [];
    switch (targetTab) {
      case TabActive.NEGOTIATION:
        return history.some((v) => v.status === "NEGOTIATE");
      case TabActive.APPROVED:
        return history.some(
          (v) => v.status === "APPROVED" || v.status === "DENIED_CLOSING"
        );
      case TabActive.BUDGET:
      default:
        // Same predicate as pre-refactor Project tab select.
        return history[0]?.status === "OPEN";
    }
  };

  if (preferredId) {
    const byId = budgetList.find((el) => el._id === preferredId);
    if (byId && matchesTab(byId)) return byId;
  }

  return budgetList.find(matchesTab);
}

type ProjectTabTemplateProps = {
  /** Troca de aba pelo mesmo fluxo do clique na barra (aba Overview). */
  onNavigateTab?: (tab: TabActive) => void;
};

type ProjectTabConfig = {
  index: TabActive;
  label: string;
  template: ComponentType<ProjectTabTemplateProps>;
  area: string;
  disabled: boolean;
  /** Quando true, a aba não aparece na barra (mantém `TabActive` estável no restante do app). */
  hidden?: boolean;
};

// A ordem da barra é a ordem do array `tabs`, não o valor do enum: a aba
// Overview vem primeiro, mas Overview e Produção ficam no fim do enum.
function getVisibleTabConfig(
  tabActive: TabActive,
  allTabs: ProjectTabConfig[]
): ProjectTabConfig {
  const position = allTabs.findIndex((t) => t.index === tabActive);
  const current = allTabs[position];
  if (current && !current.hidden) return current;
  const forward = allTabs.slice(position + 1).find((t) => !t.hidden);
  if (forward) return forward;
  const backward = allTabs
    .slice(0, Math.max(position, 0))
    .reverse()
    .find((t) => !t.hidden);
  return backward ?? allTabs.find((t) => !t.hidden) ?? allTabs[0];
}

function getPrevVisibleTabIndex(
  current: TabActive,
  allTabs: ProjectTabConfig[]
): TabActive | null {
  const position = allTabs.findIndex((t) => t.index === current);
  const prev = allTabs
    .slice(0, Math.max(position, 0))
    .reverse()
    .find((t) => !t.hidden);
  return prev ? prev.index : null;
}

function getNextVisibleTabIndex(
  current: TabActive,
  allTabs: ProjectTabConfig[]
): TabActive | null {
  const position = allTabs.findIndex((t) => t.index === current);
  if (position < 0) return null;
  const next = allTabs.slice(position + 1).find((t) => !t.hidden);
  return next ? next.index : null;
}

const StyledSpan = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  &:hover {
    opacity: 0.5;
  }
`;

const ProjectScreen = () => {
  const {
    loadProject,
    project,
    clearProject,
    notification,
    closeNotification,
    tabActive,
    setTabActive,
    unsavedSummary,
    handleSetUnsavedSummary,
    event,
    client,
  } = useProject();
  const {
    loadBudgetList,
    hasNegotiations,
    hasClosings,
    budgetList,
    activeBudget,
    detailedBudget,
    setNegotiationVersion,
    setDetailedBudget,
    setVersionInfo,
    budgetAutosaveStatus,
    hasPendingBudgetChanges,
    budgetAutosaveSuccessCount,
    runBudgetAutosave,
    hydrateActiveBudget,
    isBudgetDisabled,
  } = useBudget();

  const showBudgetBottomBar = useBudgetBottomBarVisible();

  /** Pending edits that should flush on leave — ignore when editor is read-only. */
  const hasFlushableBudgetChanges =
    hasPendingBudgetChanges && !isBudgetDisabled;

  const usesInternalScrollLayout =
    tabActive === TabActive.BUDGET ||
    tabActive === TabActive.NEGOTIATION ||
    tabActive === TabActive.APPROVED ||
    tabActive === TabActive.SERVICES ||
    tabActive === TabActive.PRODUCAO ||
    tabActive === TabActive.STORAGE;

  const { getService } = useServices();
  const {
    status: estudoAutosaveStatus,
    successCount: estudoAutosaveSuccessCount,
    errorToastCount: estudoAutosaveErrorToastCount,
    hasPending: hasPendingEstudoAutosave,
    flush: flushEstudoAutosave,
  } = useEstudoAutosaveContext();

  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const projectId = searchParams.get("projectId");
  const projectCreatedAt = project?.createdAt as string | undefined;

  const { data: lastEditor } = useProjectLastEditorAudit(
    projectId,
    !!projectId
  );

  const { data: projectTimelineAudit, isFetched: isTimelineAuditFetched } =
    useProjectTimelineAudit(projectId, projectCreatedAt);

  const lastEditorName = lastEditor?.user?.name ?? "";
  const lastEditedAt = lastEditor?.createdAt ?? project?.updatedAt;
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const unsavedSummaryRef = useRef(unsavedSummary);
  const pendingNavigationRef = useRef<any>(null);
  const budgetAutosaveToastIdRef = useRef<any>(null);
  const initializedBudgetSuccessRef = useRef(false);
  const lastToastStatusRef = useRef<string | null>(null);
  const lastSuccessCountRef = useRef(0);
  // Conta falhas consecutivas de autosave; só exibimos o toast de erro a
  // partir da 2ª falha, evitando poluição em falhas transitórias.
  const budgetAutosaveFailureCountRef = useRef(0);
  const BUDGET_AUTOSAVE_ERROR_TOAST_THRESHOLD = 2;
  const estudoAutosaveToastIdRef = useRef<ReturnType<
    typeof toast.loading
  > | null>(null);
  const initializedEstudoSuccessRef = useRef(false);
  const lastEstudoToastStatusRef = useRef<string | null>(null);
  const lastEstudoSuccessCountRef = useRef(0);
  const lastEstudoErrorToastCountRef = useRef(0);

  useEffect(() => {
    unsavedSummaryRef.current = unsavedSummary;
  }, [unsavedSummary]);

  const tabs: ProjectTabConfig[] = [
    {
      index: TabActive.OVERVIEW,
      label: "Overview",
      template: ProjectOverview,
      area: "",
      disabled: false,
      // Só existe depois que o projeto foi salvo.
      hidden: !projectId,
    },
    {
      index: TabActive.INITIAL,
      label: "Dados Cadastrais",
      template: ProjectStep,
      area: "project",
      disabled: false,
    },
    {
      index: TabActive.SUMMARY,
      label: "Resumo Financeiro",
      template: SummaryStep,
      area: "",
      disabled: false,
    },
    {
      index: TabActive.SERVICES,
      label: "Serviços",
      template: Logistica,
      area: "logistics",
      disabled: false,
    },
    {
      index: TabActive.PRODUCAO,
      label: "Produção",
      template: EstudoDeVerba,
      area: "",
      disabled: false,
    },
    {
      index: TabActive.AERIAL,
      label: "Aéreo",
      template: AerialStep,
      area: "aerial",
      disabled: false,
      hidden: true,
    },
    {
      index: TabActive.RSVP,
      label: "RSVP",
      template: RSVPStep,
      area: "rsvp",
      disabled: false,
      hidden: true,
    },
    {
      index: TabActive.CRIATIVO,
      label: "Criativo",
      template: Criativo,
      area: "creative",
      disabled: false,
      hidden: true,
    },
    {
      index: TabActive.ESTANDE,
      label: "Estande",
      template: BoothStep,
      area: "booth",
      disabled: false,
      hidden: true,
    },
    {
      index: TabActive.BUDGET,
      label: "Orçamento",
      template: BudgetStep,
      area: "budget",
      disabled: false,
    },
    {
      index: TabActive.NEGOTIATION,
      label: "Negociação",
      template: BudgetStep,
      area: "negotiation",
      disabled: !hasNegotiations,
    },
    {
      index: TabActive.APPROVED,
      label: "Fechamento",
      template: BudgetStep,
      area: "closing",
      disabled: !hasClosings,
    },
    {
      index: TabActive.STORAGE,
      label: "Repositório",
      template: ProjectStoragePage,
      area: "storage",
      disabled: false,
      hidden: true,
    },
  ];
  const visibleTabs = tabs.filter((t) => !t.hidden);
  const nextStepTab = getNextStepTab(project?.status?.status);

  const [pendingTab, setPendingTab] = useState(-1);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showNegotiationAuditModal, setShowNegotiationAuditModal] =
    useState(false);
  const [showClosingAuditModal, setShowClosingAuditModal] = useState(false);
  const [unsavedSummaryModal, setUnsavedSummaryModal] = useState(false);

  const activeTabConfig = getVisibleTabConfig(tabActive, tabs);
  const Template = activeTabConfig.template;

  const auditButtonConfig = projectId
    ? activeTabConfig.area !== "" &&
      activeTabConfig.area !== "negotiation" &&
      activeTabConfig.area !== "closing"
      ? { label: "Auditoria", action: () => setShowAuditModal(true) }
      : activeTabConfig.area === "negotiation"
      ? {
          label: "Auditoria de Negociação",
          action: () => setShowNegotiationAuditModal(true),
        }
      : activeTabConfig.area === "closing"
      ? {
          label: "Auditoria de Fechamento",
          action: () => setShowClosingAuditModal(true),
        }
      : null
    : null;

  useEffect(() => {
    const unblock = history.block((nextLocation) => {
      const hasBudgetPending = hasFlushableBudgetChanges;
      const hasEstudoPending = hasPendingEstudoAutosave();

      if (!hasBudgetPending && !hasEstudoPending) {
        return;
      }

      pendingNavigationRef.current = nextLocation;
      void (async () => {
        const budgetSuccess = hasBudgetPending
          ? await runBudgetAutosave("navigation")
          : true;
        const estudoSuccess = hasEstudoPending
          ? await flushEstudoAutosave()
          : true;

        if (budgetSuccess && estudoSuccess && pendingNavigationRef.current) {
          const target = pendingNavigationRef.current;
          pendingNavigationRef.current = null;
          unblock();
          history.push(`${target.pathname}${target.search}${target.hash}`);
        }
      })();

      return false;
    });

    return () => {
      unblock();
    };
  }, [
    history,
    hasFlushableBudgetChanges,
    runBudgetAutosave,
    hasPendingEstudoAutosave,
    flushEstudoAutosave,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const hasUnsavedSummary = !!unsavedSummaryRef.current?.active;
      const hasUnsavedBudget = hasFlushableBudgetChanges;
      const hasUnsavedEstudo = hasPendingEstudoAutosave();

      if (hasUnsavedSummary || hasUnsavedBudget || hasUnsavedEstudo) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    hasFlushableBudgetChanges,
    unsavedSummary?.active,
    hasPendingEstudoAutosave,
  ]);

  // FE-P1-08: tab switches resolve the list row by `_id` (summary-safe), then hydrate.
  const updateBudgetStateForTab = useCallback(
    (targetTab: number) => {
      const preferredId =
        activeBudget?._id || parseBudgetIdFromDetailed(detailedBudget);

      if (targetTab === TabActive.NEGOTIATION) {
        const newActiveBudget = findListBudgetForTab(
          budgetList,
          TabActive.NEGOTIATION,
          preferredId
        );
        const firstNegVersion = newActiveBudget?.versionHistory?.find(
          (el) => el.status === "NEGOTIATE"
        );

        if (newActiveBudget) {
          setNegotiationVersion(firstNegVersion?.version || 0);
          if (firstNegVersion) setVersionInfo(firstNegVersion);
          setDetailedBudget(
            `${newActiveBudget._id}-version-${firstNegVersion?.version || 1}`
          );
          // FE-P1-03: hydrate full editor doc (fetch+merge when summary-only).
          // A etapa vai junto: o ref ainda está na aba anterior neste turno.
          void hydrateActiveBudget(newActiveBudget, firstNegVersion);
        }
      }
      if (targetTab === TabActive.BUDGET) {
        const openVersion = {
          comparisonVersion: 0,
          version: 0,
          status: "OPEN",
        };
        setVersionInfo(openVersion);
        const newActiveBudget = findListBudgetForTab(
          budgetList,
          TabActive.BUDGET,
          preferredId
        );

        if (newActiveBudget) {
          setNegotiationVersion(0);
          setDetailedBudget(
            newActiveBudget._id ? `${newActiveBudget._id}-version-0` : ""
          );
          void hydrateActiveBudget(newActiveBudget, openVersion);
        }
      }
      if (targetTab === TabActive.APPROVED) {
        const newActiveBudget = findListBudgetForTab(
          budgetList,
          TabActive.APPROVED,
          preferredId
        );
        const firstApprVersion = newActiveBudget?.versionHistory?.find(
          (el) => el.status === "APPROVED" || el.status === "DENIED_CLOSING"
        );

        if (newActiveBudget) {
          const detailedBudgetId = firstApprVersion
            ? `${newActiveBudget._id}-version-${firstApprVersion.version}`
            : `${newActiveBudget._id}-version-1`;

          setNegotiationVersion(firstApprVersion?.version || 0);
          if (firstApprVersion) setVersionInfo(firstApprVersion);
          setDetailedBudget(detailedBudgetId);
          void hydrateActiveBudget(newActiveBudget, firstApprVersion);
        }
      }
    },
    [
      activeBudget?._id,
      budgetList,
      detailedBudget,
      setNegotiationVersion,
      setVersionInfo,
      setDetailedBudget,
      hydrateActiveBudget,
    ]
  );

  useEffect(() => {
    if (pendingTab >= 0) {
      // Only check if modal should be shown, don't update state yet
      if (localStorage.getItem("hideAlert") !== "0") {
        unsavedSummary?.active
          ? setUnsavedSummaryModal(true)
          : setShowAlertModal(true);
      } else {
        // If no modal needed, update state and change tab immediately
        updateBudgetStateForTab(pendingTab);
        setTabActive(pendingTab);
        setPendingTab(-1);
      }
    }
  }, [pendingTab, updateBudgetStateForTab]);

  function changeTab(status: boolean) {
    if (status && !!projectId && pendingTab >= 0) {
      // Update state first, then change tab
      updateBudgetStateForTab(pendingTab);
      setTabActive(pendingTab);
    }
    setShowAlertModal(false);
    setUnsavedSummaryModal(false);
    status && handleSetUnsavedSummary({ active: false, saveFn: null });
    setPendingTab(-1);
  }

  async function handleDecideUnsavedSummary(status: boolean) {
    if (!status) {
      changeTab(true);
      return;
    }

    const response = await unsavedSummary?.saveFn();
    if (response !== false) {
      changeTab(true);
    }
  }

  function handleChangeTab(index: any) {
    const proceedWithTabChange = async () => {
      if (hasFlushableBudgetChanges) {
        const budgetSuccess = await runBudgetAutosave("navigation");
        if (!budgetSuccess) {
          setPendingTab(index);
          return;
        }
      }

      if (hasPendingEstudoAutosave()) {
        const estudoSuccess = await flushEstudoAutosave();
        if (!estudoSuccess) {
          setPendingTab(index);
          return;
        }
      }

      setPendingTab(index);
    };

    if (projectId) {
      if (index === TabActive.NEGOTIATION && !hasNegotiations) {
        return toast.warn(
          "Esse Projeto ainda não possui nenhuma negociação ativa"
        );
      }
      if (index === TabActive.APPROVED && !hasClosings) {
        return toast.warn(
          "Esse Projeto ainda não possui nenhum fechamento ativo"
        );
      }
      void proceedWithTabChange();
    }
  }
  useEffect(() => {
    if (projectId) {
      loadProject(projectId);
      loadBudgetList(projectId);
      getService(projectId);
    }

    return clearProject();
  }, []);

  // Projeto existente abre na aba Overview. Vem depois do efeito acima (que
  // zera a aba via clearProject) e antes dos deep links (?subTab / ?tab), que
  // continuam vencendo.
  useEffect(() => {
    if (projectId) setTabActive(TabActive.OVERVIEW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousProjectIdRef = useRef(projectId);
  const budgetDeepLinkKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!projectId || previousProjectIdRef.current === projectId) {
      return;
    }
    // Troca de um projeto para outro abre o novo na Overview; o projeto
    // recém-criado (sem id anterior) continua em Dados Cadastrais.
    if (previousProjectIdRef.current) setTabActive(TabActive.OVERVIEW);
    previousProjectIdRef.current = projectId;
    loadProject(projectId);
    loadBudgetList(projectId);
    getService(projectId);
  }, [projectId, loadProject, loadBudgetList, getService, setTabActive]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("subTab") === "event") {
      setTabActive(TabActive.INITIAL);
    }
    if (params.get("tab") === "budget") {
      setTabActive(TabActive.BUDGET);
    }
  }, [location.search, setTabActive]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") !== "budget" || !budgetList?.length) {
      return;
    }
    const key = `${params.get("projectId") ?? ""}:budget`;
    if (budgetDeepLinkKeyRef.current === key) {
      return;
    }
    budgetDeepLinkKeyRef.current = key;
    setTabActive(TabActive.BUDGET);
    updateBudgetStateForTab(TabActive.BUDGET);
    params.delete("tab");
    const next = params.toString();
    history.replace({
      pathname: location.pathname,
      search: next ? `?${next}` : "",
    });
  }, [
    location.search,
    location.pathname,
    budgetList,
    setTabActive,
    updateBudgetStateForTab,
    history,
  ]);

  const projectCode = project ? `MM ${project.mmCode}.${project.yearCode}` : "";
  const projectTitle = projectId ? event?.name || projectCode : "Criar projeto";
  const headerMeta = projectId
    ? [
        client?.clientSelected?.nickname || client?.clientSelected?.name,
        normalizeProjectType(event?.type),
        formatEventLocation(event),
        formatEventPeriod(event?.startDate, event?.endDate),
      ].filter(Boolean)
    : [];

  const isBudgetTab =
    tabActive === TabActive.BUDGET ||
    tabActive === TabActive.NEGOTIATION ||
    tabActive === TabActive.APPROVED;

  useEffect(() => {
    if (!isBudgetTab) {
      lastToastStatusRef.current = null;
      return;
    }

    // Ignora se o status não mudou
    if (lastToastStatusRef.current === budgetAutosaveStatus) {
      return;
    }

    lastToastStatusRef.current = budgetAutosaveStatus;

    if (budgetAutosaveStatus === "saving") {
      if (!budgetAutosaveToastIdRef.current) {
        budgetAutosaveToastIdRef.current = toast.loading(
          "Salvando alterações..."
        );
      }
      return;
    }

    if (budgetAutosaveStatus === "error") {
      budgetAutosaveFailureCountRef.current += 1;

      // Só exibe o toast de erro a partir do limite; antes disso, apenas
      // descarta o toast de "Salvando..." silenciosamente para deixar o
      // retry automático tentar de novo.
      if (
        budgetAutosaveFailureCountRef.current <
        BUDGET_AUTOSAVE_ERROR_TOAST_THRESHOLD
      ) {
        if (budgetAutosaveToastIdRef.current) {
          toast.dismiss(budgetAutosaveToastIdRef.current);
          budgetAutosaveToastIdRef.current = null;
        }
        return;
      }

      if (budgetAutosaveToastIdRef.current) {
        toast.update(budgetAutosaveToastIdRef.current, {
          render:
            "Falha ao salvar automaticamente. O sistema tentará novamente.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
        });
        budgetAutosaveToastIdRef.current = null;
      } else {
        toast.error(
          "Falha ao salvar automaticamente. O sistema tentará novamente.",
          { autoClose: 3000 }
        );
      }
    } else if (budgetAutosaveStatus === "saved") {
      budgetAutosaveFailureCountRef.current = 0;
    }
  }, [budgetAutosaveStatus, isBudgetTab]);

  useEffect(() => {
    if (!isBudgetTab) {
      lastSuccessCountRef.current = budgetAutosaveSuccessCount;
      return;
    }

    if (!initializedBudgetSuccessRef.current) {
      initializedBudgetSuccessRef.current = true;
      lastSuccessCountRef.current = budgetAutosaveSuccessCount;
      return;
    }

    // Ignora se o successCount não mudou
    if (lastSuccessCountRef.current === budgetAutosaveSuccessCount) {
      return;
    }

    lastSuccessCountRef.current = budgetAutosaveSuccessCount;
    budgetAutosaveFailureCountRef.current = 0;

    if (budgetAutosaveToastIdRef.current) {
      toast.update(budgetAutosaveToastIdRef.current, {
        render: "Todas as alterações foram salvas",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
      budgetAutosaveToastIdRef.current = null;
    } else {
      toast.success("Todas as alterações foram salvas", {
        autoClose: 3000,
        toastId: "budget-autosave-success",
      });
    }
  }, [budgetAutosaveSuccessCount, isBudgetTab]);

  const isEstudoTab = tabActive === TabActive.PRODUCAO;

  useEffect(() => {
    if (!isEstudoTab) {
      lastEstudoToastStatusRef.current = null;
      return;
    }

    if (lastEstudoToastStatusRef.current === estudoAutosaveStatus) {
      return;
    }

    lastEstudoToastStatusRef.current = estudoAutosaveStatus;

    if (estudoAutosaveStatus === "saving") {
      if (!estudoAutosaveToastIdRef.current) {
        estudoAutosaveToastIdRef.current = toast.loading(
          "Salvando alterações..."
        );
      }
      return;
    }

    if (estudoAutosaveStatus === "error") {
      if (estudoAutosaveToastIdRef.current) {
        toast.dismiss(estudoAutosaveToastIdRef.current);
        estudoAutosaveToastIdRef.current = null;
      }
      return;
    }

    // Save manual não incrementa o contador. Se o loading de um autosave
    // ainda estiver na tela, tira ele aqui — o sucesso automático atualiza
    // esse mesmo toast no efeito do contador.
    if (
      estudoAutosaveStatus === "saved" &&
      estudoAutosaveToastIdRef.current &&
      lastEstudoSuccessCountRef.current === estudoAutosaveSuccessCount
    ) {
      toast.dismiss(estudoAutosaveToastIdRef.current);
      estudoAutosaveToastIdRef.current = null;
    }
  }, [estudoAutosaveStatus, estudoAutosaveSuccessCount, isEstudoTab]);

  useEffect(() => {
    if (!isEstudoTab) {
      lastEstudoSuccessCountRef.current = estudoAutosaveSuccessCount;
      return;
    }

    if (!initializedEstudoSuccessRef.current) {
      initializedEstudoSuccessRef.current = true;
      lastEstudoSuccessCountRef.current = estudoAutosaveSuccessCount;
      return;
    }

    if (lastEstudoSuccessCountRef.current === estudoAutosaveSuccessCount) {
      return;
    }

    lastEstudoSuccessCountRef.current = estudoAutosaveSuccessCount;

    if (estudoAutosaveToastIdRef.current) {
      toast.update(estudoAutosaveToastIdRef.current, {
        render: "Todas as alterações foram salvas",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
      estudoAutosaveToastIdRef.current = null;
    } else {
      toast.success("Todas as alterações foram salvas", {
        autoClose: 3000,
        toastId: "estudo-autosave-success",
      });
    }
  }, [estudoAutosaveSuccessCount, isEstudoTab]);

  useEffect(() => {
    if (!isEstudoTab) {
      lastEstudoErrorToastCountRef.current = estudoAutosaveErrorToastCount;
      return;
    }

    if (
      lastEstudoErrorToastCountRef.current === estudoAutosaveErrorToastCount
    ) {
      return;
    }

    lastEstudoErrorToastCountRef.current = estudoAutosaveErrorToastCount;
    if (estudoAutosaveErrorToastCount === 0) return;

    const message =
      "Falha ao salvar automaticamente. O sistema tentará novamente.";
    if (estudoAutosaveToastIdRef.current) {
      toast.update(estudoAutosaveToastIdRef.current, {
        render: message,
        type: "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
      estudoAutosaveToastIdRef.current = null;
    } else {
      toast.error(message, { autoClose: 3000 });
    }
  }, [estudoAutosaveErrorToastCount, isEstudoTab]);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      py={2}
      px={3}
      bgcolor={"#F9FCFF"}
      flex={1}
      minHeight={0}
      minWidth={0}
      maxWidth="100%"
    >
      {notification?.open && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={6000}
          open={notification.open}
          onClose={closeNotification}
          TransitionComponent={(props) => <Slide {...props} direction="left" />}
        >
          <Alert
            onClose={closeNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}

      {showUpdateStatus && (
        <ProjectStatusModal
          isOpen={showUpdateStatus}
          onClose={() => setShowUpdateStatus(false)}
          status={project?.status}
        />
      )}
      {showAuditModal && (
        <AuditModal
          isOpen={showAuditModal}
          onClose={() => setShowAuditModal(false)}
          area={activeTabConfig.area}
          label={activeTabConfig.label}
          variant={
            activeTabConfig.area === "project" &&
            activeTabConfig.label === "Dados Cadastrais"
              ? "project-registration"
              : "default"
          }
        />
      )}
      {showNegotiationAuditModal && (
        <NegotiationAuditModal
          isOpen={showNegotiationAuditModal}
          onClose={() => setShowNegotiationAuditModal(false)}
          budgetId={activeBudget?._id}
        />
      )}
      {showClosingAuditModal && (
        <ClosingAuditModal
          open={showClosingAuditModal}
          onClose={() => setShowClosingAuditModal(false)}
          projectId={projectId || ""}
          budgetId={activeBudget?._id}
        />
      )}

      <Box display={"flex"} flexDirection={"column"} gap={1} mb={2}>
        <Box
          component="nav"
          aria-label="breadcrumb"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            fontSize: 13,
            color: "#5B6573",
          }}
        >
          <Box
            component="span"
            onClick={() => history.push("/projetos")}
            sx={{
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Projetos
          </Box>
          <span aria-hidden>›</span>
          {projectId ? (
            <>
              <span>{projectCode}</span>
              <span aria-hidden>›</span>
              <Box component="span" sx={{ color: "#1F2733", fontWeight: 500 }}>
                {activeTabConfig.label}
              </Box>
            </>
          ) : (
            <Box component="span" sx={{ color: "#1F2733", fontWeight: 500 }}>
              Criar projeto
            </Box>
          )}
        </Box>

        {/* Título, status e última edição na mesma linha (o botão de
            status logo após o título, a última edição à direita dele). */}
        <Box
          display={"flex"}
          alignItems={"flex-end"}
          flexWrap={"wrap"}
          columnGap={3}
          rowGap={1}
        >
          <Typography
            component="h1"
            sx={{ m: 0, fontSize: 28, fontWeight: 500, lineHeight: "36px" }}
          >
            {projectTitle}
          </Typography>

          {projectId && project?.status && (
            <Box>
              <Typography sx={{ fontSize: 12, color: "#5B6573", mb: 0.5 }}>
                Status atual
              </Typography>
              <Box
                role="button"
                aria-label="Alterar status do projeto"
                onClick={() => setShowUpdateStatus(true)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  height: 36,
                  px: 1.75,
                  cursor: "pointer",
                  borderRadius: "6px",
                  fontSize: 15,
                  fontWeight: 500,
                  bgcolor: "#E3ECF8",
                  color: "#0D47A1",
                  "&:hover": { bgcolor: "#D5E3F5" },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "currentColor",
                    flexShrink: 0,
                  }}
                />
                {
                  BRIEFING_STATUS_OPTIONS.find(
                    (item) => item.value === project.status.status
                  )?.text
                }
                <MdEdit size={14} />
              </Box>
            </Box>
          )}

          {projectId && project && lastEditedAt && (
            <Typography
              sx={{
                fontSize: 13,
                color: "#5B6573",
                height: 36,
                display: "flex",
                alignItems: "center",
              }}
            >
              {`Última edição ${format(
                new Date(lastEditedAt),
                "dd/MM, HH:mm"
              )}${lastEditorName ? `, por ${lastEditorName}` : ""}`}
            </Typography>
          )}
        </Box>

        {projectId && project && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px 16px",
              fontSize: 14,
              color: "#5B6573",
            }}
          >
            <Box component="span" sx={{ color: "#1F2733", fontWeight: 500 }}>
              {projectCode}
            </Box>
            {headerMeta.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </Box>
        )}

        {!projectId && (
          <Typography variant={"caption"}>
            Adicione as informações de um projeto
          </Typography>
        )}
      </Box>

      {projectId && projectCreatedAt && isTimelineAuditFetched && (
        <ProjectStatusTimeline
          createdAt={projectCreatedAt}
          currentStatus={project?.status?.status}
          currentJustification={project?.status?.justification}
          audits={projectTimelineAudit ?? []}
        />
      )}
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          mt: 2,
          boxShadow: "0px 4px 18px 0px #4B465C1A",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <AlertModal
          handleClose={() => changeTab(false)}
          isOpen={showAlertModal}
          makeDecision={changeTab}
        />

        <UnsavedChangesModal
          handleClose={() => changeTab(false)}
          isOpen={unsavedSummaryModal}
          makeDecision={handleDecideUnsavedSummary}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "#fff",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tabs
            value={tabActive}
            onChange={(e, index) => handleChangeTab(index)}
            indicatorColor="primary"
            sx={{ textTransform: "none", flex: 1, minHeight: 48 }}
          >
            {visibleTabs.map((tab) => (
              <Tab
                value={tab.index}
                disabled={tab.disabled}
                key={tab.index}
                label={
                  tab.index === nextStepTab && tab.index !== tabActive ? (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      {tab.label}
                      <Box
                        component="span"
                        data-testid="next-step-indicator"
                        title="Próximo passo"
                        aria-label="Próximo passo"
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "#E8710A",
                        }}
                      />
                    </Box>
                  ) : (
                    tab.label
                  )
                }
                sx={{
                  textTransform: "none",
                  background: tab.disabled ? "#e7e7e7e7" : "",
                  "&:disabled": {
                    background: "red",
                  },
                }}
              />
            ))}
          </Tabs>

          {auditButtonConfig && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<MdOutlinePolicy />}
              onClick={auditButtonConfig.action}
              sx={{
                flexShrink: 0,
                textTransform: "none",
                mx: 2,
                my: 1,
              }}
            >
              {auditButtonConfig.label}
            </Button>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: "#F9FCFF",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              width: "100%",
              maxWidth: "100%",
              ...(usesInternalScrollLayout
                ? {
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }
                : { overflow: "auto" }),
            }}
          >
            <Template onNavigateTab={handleChangeTab} />
          </Box>
          {showBudgetBottomBar ? (
            <BudgetBottomBar />
          ) : (
            projectId && (
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"flex-end"}
                px={10}
                py={2}
                gap={2}
                sx={{ flexShrink: 0 }}
              >
                {getPrevVisibleTabIndex(tabActive, tabs) !== null && (
                  <StyledSpan
                    onClick={() =>
                      handleChangeTab(getPrevVisibleTabIndex(tabActive, tabs)!)
                    }
                  >
                    <FaCaretLeft />
                    Voltar
                  </StyledSpan>
                )}
                {getNextVisibleTabIndex(tabActive, tabs) !== null && (
                  <StyledSpan
                    onClick={() =>
                      handleChangeTab(getNextVisibleTabIndex(tabActive, tabs)!)
                    }
                  >
                    Avançar
                    <FaCaretRight />
                  </StyledSpan>
                )}
              </Box>
            )
          )}
        </Box>
      </Card>
    </Box>
  );
};

const Project = () => (
  <EstudoAutosaveProvider>
    <ProjectScreen />
  </EstudoAutosaveProvider>
);

export default Project;
